/**
 * Leads Service
 * Handles all lead-related API operations
 *
 * CONSOLIDADO: Usa tipos oficiales de:
 * - API types: src/types/api/lead.api.types.ts
 * - Frontend types: src/types/lead.ts
 * - Adapter: src/services/api/leadAdapter.ts
 */

import { apiClient } from '@/lib/api-client';
import { API_ENDPOINTS } from '@/config/api.config';
import { buildMLContext } from '@/utils/ml-context';
import {
  APILead,
  APILeadListParams,
  APILeadCreateData,
  APILeadUpdateData,
  APILeadStats,
  APIBulkAssignData,
  APIBulkStatusChangeData,
  APIBulkDeleteData,
  APIPerformanceBySource,
  APILeadListResponse,
} from '@/types/api/lead.api.types';
import { Lead, PriorityLeadsResponse } from '@/types/lead';
import { adaptAPILeadsToFrontend, adaptAPILeadToFrontend, adaptFrontendLeadToAPI } from './leadAdapter';
import { LeadActivitiesResponse, LeadActivitiesParams } from '@/types/leadActivity';

// ========================================
// RE-EXPORT API TYPES for backward compatibility
// ========================================
export type {
  APILead as Lead, // For legacy code that imports Lead from here
  APILeadListParams as LeadListParams,
  APILeadCreateData as LeadCreateData,
  APILeadUpdateData as LeadUpdateData,
  APILeadStats as LeadStats,
  APIBulkAssignData as BulkAssignData,
  APIBulkStatusChangeData as BulkStatusChangeData,
  APIBulkDeleteData as BulkDeleteData,
  APIPerformanceBySource as PerformanceBySource,
}

export interface PotentialDuplicate {
  lead_id: string;
  lead_number: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  project_name: string | null;
  source: string;
  lead_score: number;
  match_type: 'email' | 'phone' | 'document';
  confidence: number;
  same_project: boolean;
}

export interface UnifyLeadResponse {
  status: string;
  message: string;
  lead: APILead;
  stats: {
    conversations_moved: number;
    conversations_merged: number;
    messages_moved: number;
    status_history_moved: number;
    submissions_moved: number;
    activities_moved: number;
    quotes_moved: number;
    calendar_events_moved: number;
  };
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface ScoreBreakdownDetail {
  label: string;
  value: string;
  ok: boolean;
  points?: number;
  /** 'header' = section divider, 'info' = informational (no check/cross), undefined = evaluation */
  type?: 'header' | 'info';
}

export interface ScoreBreakdownCriterion {
  raw: number;
  weight: number;
  weighted: number;
  details?: ScoreBreakdownDetail[];
}

export interface DataQualityBreakdownItem {
  label: string;
  value: string;
  ok: boolean;
  points: number;
  max_points?: number;
  key?: string;
  manual?: boolean;
}

export interface EngagementBreakdownItem {
  label: string;
  value: string;
  ok: boolean;
  points: number;
  subscore: string;
  key?: string;
  manual?: boolean;
}

export interface ScoreBreakdownResponse {
  lead_id: string;
  lead_score: number;
  source: string;
  // V2 3D scoring fields
  scoring_version?: number;
  data_quality_score?: number;
  profile_score?: number;
  engagement_score?: number;
  priority_matrix_label?: string;
  data_quality?: {
    score: number;
    breakdown: DataQualityBreakdownItem[];
  };
  engagement?: {
    score: number;
    raw_score: number;
    decay_applied: number | null;
    breakdown: EngagementBreakdownItem[];
  };
  // Profile breakdown (existing structure)
  breakdown: {
    total_score: number;
    breakdown: {
      budget: ScoreBreakdownCriterion;
      timeline: ScoreBreakdownCriterion;
      zone: ScoreBreakdownCriterion;
      source: ScoreBreakdownCriterion;
      completeness: ScoreBreakdownCriterion;
      contact: ScoreBreakdownCriterion;
    };
  };
  data_quality_overrides?: Record<string, boolean>;
  engagement_overrides?: Record<string, boolean>;
  voice_agent_session?: {
    session_id: string;
    scoring: Record<string, any>;
    lead_info: Record<string, any>;
    transcript: Array<{ role: string; content: string }>;
    timestamp: string;
  } | null;
}

/**
 * Leads Service
 */
export const leadsService = {
  /**
   * Get all leads with pagination and filters
   */
  async getLeads(params?: LeadListParams): Promise<PaginatedResponse<Lead>> {

    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => queryParams.append(key, v.toString()));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }

    const endpoint = params
      ? `${API_ENDPOINTS.LEADS.BASE}?${queryParams.toString()}`
      : API_ENDPOINTS.LEADS.BASE;


    try {
      const response = await apiClient.get<PaginatedResponse<APILead>>(endpoint);

      // Transform API leads to frontend format
      const transformedResults = adaptAPILeadsToFrontend(response.results);

      return {
        ...response,
        results: transformedResults
      };
    } catch (error) {
      console.error('❌ [leadsService] Error getting leads:', error);
      throw error;
    }
  },

  /**
   * Get scoring data for 3D scatter plot (lightweight — only scoring fields)
   */
  async getScoringScatter(params?: { project_id?: string; company_id?: string }): Promise<{ id: string; name: string; data_quality_score: number; profile_score: number; engagement_score: number; priority_matrix_label: string }[]> {
    const queryParams = new URLSearchParams();
    if (params?.project_id) {
      queryParams.append('project_id', params.project_id);
    }
    if (params?.company_id) {
      queryParams.append('company_id', params.company_id);
    }
    const url = queryParams.toString()
      ? `${API_ENDPOINTS.LEADS.SCORING_SCATTER}?${queryParams.toString()}`
      : API_ENDPOINTS.LEADS.SCORING_SCATTER;
    return apiClient.get(url);
  },

  async getScoringHistory(params?: { project_id?: string; company_id?: string }): Promise<{ lead_id: string; dq: number; profile: number; engagement: number; label: string; date: string }[]> {
    const queryParams = new URLSearchParams();
    if (params?.project_id) queryParams.append('project_id', params.project_id);
    if (params?.company_id) queryParams.append('company_id', params.company_id);
    const qs = queryParams.toString();
    return apiClient.get(`/api/leads/scoring-history/${qs ? `?${qs}` : ''}`);
  },

  /**
   * Get lead by ID
   */
  async getLeadById(id: string): Promise<Lead> {
    const apiLead = await apiClient.get<APILead>(API_ENDPOINTS.LEADS.DETAIL(id));
    return adaptAPILeadToFrontend(apiLead);
  },

  /**
   * Create new lead
   */
  async createLead(data: LeadCreateData): Promise<Lead> {
    const apiLead = await apiClient.post<APILead>(API_ENDPOINTS.LEADS.BASE, data);
    return adaptAPILeadToFrontend(apiLead);
  },

  /**
   * Update lead
   */
  async updateLead(id: string, data: LeadUpdateData): Promise<Lead> {
    const apiLead = await apiClient.patch<APILead>(API_ENDPOINTS.LEADS.DETAIL(id), data);
    return adaptAPILeadToFrontend(apiLead);
  },

  /**
   * Update lead and return raw API response (includes potential_duplicate info)
   */
  async updateLeadWithDedupCheck(id: string, data: LeadUpdateData): Promise<APILead & { potential_duplicate?: PotentialDuplicate }> {
    return apiClient.patch<APILead & { potential_duplicate?: PotentialDuplicate }>(
      API_ENDPOINTS.LEADS.DETAIL(id),
      data
    );
  },

  /**
   * Unify source lead into target lead
   */
  async unifyLead(sourceLeadId: string, targetLeadId: string, matchType?: string): Promise<UnifyLeadResponse> {
    return apiClient.post<UnifyLeadResponse>(
      API_ENDPOINTS.LEADS.UNIFY(sourceLeadId),
      { target_lead_id: targetLeadId, match_type: matchType || 'manual' }
    );
  },

  /**
   * Delete lead (soft delete)
   */
  async deleteLead(id: string): Promise<void> {
    return apiClient.delete<void>(API_ENDPOINTS.LEADS.DETAIL(id));
  },

  /**
   * Assign lead to user
   */
  async assignLead(id: string, userId: string): Promise<Lead> {
    const apiLead = await apiClient.post<APILead>(
      API_ENDPOINTS.LEADS.ASSIGN(id),
      { user_id: userId }
    );
    return adaptAPILeadToFrontend(apiLead);
  },

  /**
   * Change lead status
   */
  async changeStatus(id: string, status: string): Promise<Lead> {
    const apiLead = await apiClient.post<APILead>(
      API_ENDPOINTS.LEADS.CHANGE_STATUS(id),
      { status }
    );
    return adaptAPILeadToFrontend(apiLead);
  },

  /**
   * Update lead score
   */
  async updateScore(id: string, score: number): Promise<Lead> {
    const apiLead = await apiClient.post<APILead>(
      API_ENDPOINTS.LEADS.UPDATE_SCORE(id),
      { score }
    );
    return adaptAPILeadToFrontend(apiLead);
  },

  /**
   * Get score breakdown for a lead
   */
  async getScoreBreakdown(id: string): Promise<ScoreBreakdownResponse> {
    return apiClient.get<ScoreBreakdownResponse>(
      API_ENDPOINTS.LEADS.SCORE_BREAKDOWN(id)
    );
  },

  /**
   * Recalculate lead score using config-based scoring
   */
  async recalculateScore(id: string): Promise<{ lead_score: number; lead: APILead }> {
    return apiClient.post(API_ENDPOINTS.LEADS.RECALCULATE_SCORE(id));
  },

  /**
   * Verificar o desmarcar manualmente un item de calidad de datos
   */
  async verifyDqItem(id: string, itemKey: string, verified: boolean): Promise<{ lead_score: number; data_quality_overrides: Record<string, boolean> }> {
    return apiClient.post(`/leads/${id}/verify-dq-item/`, { item_key: itemKey, verified });
  },

  /**
   * Verify or unverify an engagement (nivel de interés) item manually
   */
  async verifyEngagementItem(id: string, itemKey: string, verified: boolean): Promise<{ lead_score: number; engagement_overrides: Record<string, boolean> }> {
    return apiClient.post(`/leads/${id}/verify-engagement-item/`, { item_key: itemKey, verified });
  },

  /**
   * Add note to lead
   */
  async addNote(id: string, note: string): Promise<Lead> {
    const apiLead = await apiClient.post<APILead>(
      API_ENDPOINTS.LEADS.ADD_NOTE(id),
      { note }
    );
    return adaptAPILeadToFrontend(apiLead);
  },

  /**
   * Get lead timeline
   */
  async getTimeline(id: string): Promise<any[]> {
    return apiClient.get<any[]>(API_ENDPOINTS.LEADS.TIMELINE(id));
  },

  /**
   * Get lead status transition history
   */
  async getStatusHistory(id: string): Promise<Array<{
    status: string;
    changed_at: string;
    reason: string;
    notes: string;
    quote_number: string;
  }>> {
    return apiClient.get(API_ENDPOINTS.LEADS.STATUS_HISTORY(id));
  },

  /**
   * Get lead statistics
   */
  /**
   * Get leads with active alerts for Panel de Seguimiento.
   * Optimized backend endpoint — only returns leads that have alerts.
   */
  async getPanelAlerts(params?: Record<string, string>): Promise<{ count: number; results: any[] }> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
    }
    const endpoint = queryParams.toString()
      ? `${API_ENDPOINTS.LEADS.PANEL_ALERTS}?${queryParams.toString()}`
      : API_ENDPOINTS.LEADS.PANEL_ALERTS;
    return apiClient.get(endpoint);
  },

  async getStats(params?: Record<string, any>): Promise<LeadStats> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    const endpoint = queryParams.toString()
      ? `${API_ENDPOINTS.LEADS.STATS}?${queryParams.toString()}`
      : API_ENDPOINTS.LEADS.STATS;
    return apiClient.get<LeadStats>(endpoint);
  },

  /**
   * Get hot leads (high priority/score)
   */
  async getHotLeads(): Promise<Lead[]> {
    const response = await apiClient.get<PaginatedResponse<APILead>>(
      API_ENDPOINTS.LEADS.HOT_LEADS
    );
    return adaptAPILeadsToFrontend(response.results || []);
  },

  /**
   * Get priority leads for dashboard with engagement data and recommendations
   * Returns max 5 leads ordered by priority score
   */
  async getPriorityLeads(params?: { project_id?: string }): Promise<PriorityLeadsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.project_id) {
      queryParams.append('project_id', params.project_id);
    }
    const endpoint = queryParams.toString()
      ? `${API_ENDPOINTS.LEADS.PRIORITY_LEADS}?${queryParams.toString()}`
      : API_ENDPOINTS.LEADS.PRIORITY_LEADS;

    return apiClient.get<PriorityLeadsResponse>(endpoint);
  },

  /**
   * Get duplicate leads
   */
  async getDuplicates(): Promise<any[]> {
    return apiClient.get<any[]>(API_ENDPOINTS.LEADS.DUPLICATES);
  },

  /**
   * Get upcoming follow-ups
   */
  async getUpcomingFollowups(): Promise<Lead[]> {
    const response = await apiClient.get<PaginatedResponse<APILead>>(
      API_ENDPOINTS.LEADS.UPCOMING_FOLLOWUPS
    );
    return adaptAPILeadsToFrontend(response.results || []);
  },

  /**
   * Get overdue follow-ups
   */
  async getOverdueFollowups(): Promise<Lead[]> {
    const response = await apiClient.get<PaginatedResponse<APILead>>(
      API_ENDPOINTS.LEADS.OVERDUE_FOLLOWUPS
    );
    return adaptAPILeadsToFrontend(response.results || []);
  },

  /**
   * Bulk assign leads to user
   */
  async bulkAssign(data: BulkAssignData): Promise<{ message: string; updated_count: number }> {
    return apiClient.post(API_ENDPOINTS.LEADS.BULK_ASSIGN, data);
  },

  /**
   * Bulk change status of leads
   */
  async bulkStatusChange(data: BulkStatusChangeData): Promise<{ message: string; updated_count: number }> {
    return apiClient.post(API_ENDPOINTS.LEADS.BULK_STATUS_CHANGE, data);
  },

  /**
   * Bulk delete leads (soft delete)
   */
  async bulkDelete(data: { lead_ids: string[] }): Promise<{ message: string; deleted_count: number }> {
    return apiClient.post(API_ENDPOINTS.LEADS.BULK_DELETE, data);
  },

  /**
   * Bulk HARD delete leads (permanently removes from database)
   * Only available to OWLY platform admins
   */
  async bulkHardDelete(data: { lead_ids: string[] }): Promise<{ message: string; deleted_count: number }> {
    return apiClient.post(API_ENDPOINTS.LEADS.BULK_HARD_DELETE, data);
  },

  /**
   * Bulk change stage of leads (pipeline stage)
   */
  async bulkStageChange(data: { lead_ids: string[]; stage_id: string }): Promise<{ message: string; updated_count: number }> {
    return apiClient.post(API_ENDPOINTS.LEADS.BULK_STAGE_CHANGE, data);
  },

  /**
   * Bulk add/remove tags from leads
   */
  async bulkTags(data: { lead_ids: string[]; add_tags?: string[]; remove_tags?: string[] }): Promise<{ message: string; updated_count: number }> {
    return apiClient.post('/leads/bulk_tags/', data);
  },

  /**
   * Bulk mark leads as test. Sets lead.is_test=true so they get hidden
   * from the default leads list, dashboards, and don't trigger automations
   * or scoring as real prospects.
   */
  async bulkMarkTest(data: { lead_ids: string[]; reason?: string }): Promise<{ message: string; updated_count: number }> {
    return apiClient.post('/leads/bulk_mark_test/', data);
  },

  /**
   * Bulk unmark test leads. Admin-only on the backend — used to recover
   * a real lead that was flagged as test by mistake (or by an auto-flag
   * from a partner-API key still in test mode).
   */
  async bulkUnmarkTest(data: { lead_ids: string[]; reason?: string }): Promise<{ message: string; updated_count: number }> {
    return apiClient.post('/leads/bulk_unmark_test/', data);
  },

  /**
   * Get performance by source
   */
  async getPerformanceBySource(): Promise<PerformanceBySource[]> {
    return apiClient.get<PerformanceBySource[]>(API_ENDPOINTS.LEADS.PERFORMANCE_BY_SOURCE);
  },

  /**
   * Get lead activities (timeline)
   */
  async getActivities(leadId: string, params?: LeadActivitiesParams): Promise<LeadActivitiesResponse> {
    const queryParams = new URLSearchParams();

    if (params?.activity_type) {
      queryParams.append('activity_type', params.activity_type);
    }
    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }

    const endpoint = queryParams.toString()
      ? `/leads/${leadId}/activities/?${queryParams.toString()}`
      : `/leads/${leadId}/activities/`;

    return apiClient.get<LeadActivitiesResponse>(endpoint);
  },

  /**
   * Log a completed action with a lead (call, whatsapp, etc.)
   * Updates last_contact_date and creates an activity record
   */
  async logAction(leadId: string, data: LogActionData): Promise<LogActionResponse> {
    const endpoint = `/leads/${leadId}/log_action/`;
    const enrichedData = {
      ...data,
      metadata: {
        ...data.metadata,
        ml_context: buildMLContext(),
      },
    };
    return apiClient.post<LogActionResponse>(endpoint, enrichedData);
  },

  /**
   * Convert lead to client
   * Creates ClientUser and ClientUnitOwnership from accepted quote
   */
  async convertToClient(leadId: string, data: ConvertToClientData): Promise<ConvertToClientResponse> {
    const endpoint = API_ENDPOINTS.LEADS.CONVERT_TO_CLIENT(leadId);
    return apiClient.post<ConvertToClientResponse>(endpoint, data);
  },

  /**
   * Get conversion status for a lead
   * Check if lead can be converted and get existing ownerships
   */
  async getConversionStatus(leadId: string, quoteId?: string): Promise<ConversionStatusResponse> {
    const endpoint = quoteId
      ? `${API_ENDPOINTS.LEADS.CONVERSION_STATUS(leadId)}?quote_id=${quoteId}`
      : API_ENDPOINTS.LEADS.CONVERSION_STATUS(leadId);
    return apiClient.get<ConversionStatusResponse>(endpoint);
  },

  /**
   * Get unified bitacora (timeline) for a lead.
   * Merges ChangeLog + StatusHistory + Activity server-side.
   */
  async getBitacora(leadId: string, params?: { page?: number; page_size?: number; category?: string }): Promise<PaginatedResponse<BitacoraAPIEntry>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.page_size) searchParams.set('page_size', String(params.page_size));
    if (params?.category) searchParams.set('category', params.category);
    const query = searchParams.toString();
    return apiClient.get<PaginatedResponse<BitacoraAPIEntry>>(
      `${API_ENDPOINTS.LEADS.BITACORA(leadId)}${query ? '?' + query : ''}`
    );
  },

  /**
   * Get playbook checklist for a lead at a specific stage.
   * Returns tasks with completion status, SLA info, and next task.
   */
  async getStageChecklist(leadId: string, stageId: string): Promise<StageChecklistResponse> {
    return apiClient.get<StageChecklistResponse>(
      `/leads/${leadId}/stage_checklist/${stageId}/`
    );
  },

  /**
   * Complete a playbook task for a lead (dual-write: Activity + LeadActivity).
   */
  async completeStageTask(
    leadId: string,
    taskId: string,
    payload: { outcome_code?: string; notes?: string; duration_minutes?: number }
  ): Promise<CompleteStageTaskResponse> {
    return apiClient.post<CompleteStageTaskResponse>(
      `/leads/${leadId}/complete_stage_task/${taskId}/`,
      payload
    );
  },

  /**
   * Get field-level change log for a lead (audit trail)
   */
  async getChangeLog(leadId: string, params?: { field_name?: string; page?: number }): Promise<PaginatedResponse<LeadChangeLogEntry>> {
    const queryParams = new URLSearchParams();
    if (params?.field_name) queryParams.append('field_name', params.field_name);
    if (params?.page) queryParams.append('page', params.page.toString());

    const endpoint = queryParams.toString()
      ? `${API_ENDPOINTS.LEADS.CHANGE_LOG(leadId)}?${queryParams.toString()}`
      : API_ENDPOINTS.LEADS.CHANGE_LOG(leadId);

    return apiClient.get<PaginatedResponse<LeadChangeLogEntry>>(endpoint);
  },

  // ── Managed Alerts (Panel de Seguimiento) ──

  /**
   * Registrar una alerta como gestionada
   */
  async createManagedAlert(data: {
    lead_id: string;
    alert_type: string;
    action_taken: string;
    quote_id?: string | null;
    project_id?: string | null;
    notes?: string;
    metadata?: Record<string, unknown>;
  }): Promise<any> {
    return apiClient.post(API_ENDPOINTS.LEADS.MANAGED_ALERTS, data);
  },

  /**
   * Obtener stats de alertas gestionadas (hoy por defecto)
   */
  async getManagedAlertStats(params?: {
    project_id?: string;
    date_from?: string;
  }): Promise<{
    total_managed: number;
    date_from: string;
    by_type: Record<string, number>;
    by_action: Record<string, number>;
  }> {
    const queryParams = new URLSearchParams();
    if (params?.project_id) queryParams.append('project_id', params.project_id);
    if (params?.date_from) queryParams.append('date_from', params.date_from);
    const qs = queryParams.toString();
    return apiClient.get(`${API_ENDPOINTS.LEADS.MANAGED_ALERTS_STATS}${qs ? `?${qs}` : ''}`);
  },
};

// Types for unified bitacora endpoint
export interface BitacoraAPIEntry {
  id: string;
  source: 'changelog' | 'status_history' | 'activity';
  date: string;
  category: string;
  title: string;
  description: string;
  actor: string | null;
  activity_type: string | null;
  field_name: string | null;
  metadata: Record<string, unknown>;
  child_notes?: Array<{
    id: string;
    date: string;
    description: string;
    actor: string | null;
  }>;
}

// Types for Stage Checklist (Playbook tasks per lead per stage)
export interface ChecklistTask {
  id: string;
  code: string;
  name: string;
  description: string;
  order_position: number;
  executor_type: string;
  task_owner: string;
  is_required: boolean;
  is_system_auto: boolean;
  is_completed: boolean;
  is_next: boolean;
  completed_at: string | null;
  completed_by: string | null;
  activity_nature: string;
  delivery_channels: Array<{ channel: string; role: string; content_type: string; target: string }>;
  delivery_strategy: string | null;
  possible_outcomes: Array<{ code: string; label: string; icon?: string; color?: string }>;
  icon: string;
  color: string;
}

export interface SlaInfo {
  max_hours: number;
  hours_elapsed: number;
  status: 'on_track' | 'warning' | 'overdue';
  warning_percent: number;
  escalate_to: string | null;
}

export interface StageChecklistResponse {
  stage_id: string;
  stage_name: string;
  stage_semantic_id: string;
  zone_group: string;
  total_tasks: number;
  completed_tasks: number;
  progress_percent: number;
  sla: SlaInfo | null;
  next_task: { id: string; code: string; name: string } | null;
  tasks: ChecklistTask[];
}

export interface CompleteStageTaskResponse {
  message: string;
  activity_id: string;
  task_id: string;
  task_code: string;
  outcome_code: string;
  completed_at: string;
  already_completed?: boolean;
}

// Types for logAction
export interface LogActionData {
  action_type: 'call' | 'whatsapp' | 'email' | 'meeting' | 'visit_scheduled' | 'visit_completed' | 'message' | 'note';
  title?: string;
  description?: string;
  outcome?: 'contacted' | 'no_answer' | 'scheduled' | 'not_interested';
  metadata?: Record<string, any>;
  parent_activity_id?: string;
}

export interface LogActionResponse {
  success: boolean;
  message: string;
  lead: Lead;
  activity: {
    id: string;
    activity_type: string;
    activity_type_display: string;
    title: string;
    description: string;
    activity_date: string;
  };
  next_priority_lead: Lead | null;
}

// Types for convertToClient
export interface ConvertToClientData {
  /** Cotización única a convertir (legacy — un solo inmueble). */
  quote_id: string;
  /** Cotizaciones a convertir cuando el lead compra varias unidades.
   *  Si se envía, tiene prioridad sobre quote_id en el backend. */
  quote_ids?: string[];
  send_invitation?: boolean;
  exit_notes?: string;
}

export interface ConvertToClientResponse {
  success: boolean;
  message: string;
  client_user: {
    id: string;
    document_number: string;
    full_name: string;
    email: string | null;
    is_active: boolean;
  };
  ownership: {
    id: string;
    unit_code: string;
    status: string;
    pipeline_stage: string | null;
  };
  is_new_client: boolean;
  invitation_sent: boolean;
}

export interface ConversionStatusResponse {
  lead_id: string;
  lead_number: string;
  is_converted: boolean;
  conversion_date: string | null;
  has_document_number: boolean;
  existing_ownerships: Array<{
    id: string;
    unit_code: string;
    client_id: string;
    client_name: string;
    status: string;
  }>;
  quote?: {
    id: string;
    quote_number: string;
    status: string;
    unit_code: string | null;
    can_convert: boolean;
    validation_errors: string[];
  };
  // Quote inline actions
  has_accepted_quote: boolean;
  needs_quote_action: boolean;
  latest_active_quote_id: string | null;
  latest_active_quote_number: string | null;
  accepted_quotes: Array<{
    id: string;
    quote_number: string;
    unit_code: string | null;
    unit_name: string | null;
    total: number;
    project_name: string | null;
  }>;
  active_quotes: Array<{
    id: string;
    quote_number: string;
    status: string;
    unit_code: string | null;
    total: number;
  }>;
  // Unit info for inline reserve action (legacy — first unit of units_info)
  unit_info: {
    id: string;
    code: string;
    name: string;
    status: string;
    reserved_by_this_lead: boolean;
    sold_to_this_lead?: boolean;
    is_fraction?: boolean;
    fraction_id?: string | null;
  } | null;
  // All distinct units referenced by the lead's accepted/active quotes.
  // Each entry lists the quote numbers that reference it, so the advisor
  // can see the full picture when the lead has multiple quotes.
  units_info?: Array<{
    id: string;
    code: string;
    name: string;
    status: string;
    reserved_by_this_lead: boolean;
    sold_to_this_lead: boolean;
    is_fraction: boolean;
    fraction_id: string | null;
    quote_ids: string[];
    quote_numbers: string[];
  }>;
  // Required documents
  required_documents?: Array<{
    document_type: string;
    document_type_display: string;
    category: string;
    category_display: string;
    is_uploaded: boolean;
    label: string;
  }>;
  missing_documents_count?: number;
}

// Types for change log (field-level audit trail)
export interface LeadChangeLogEntry {
  id: string;
  field_name: string;
  old_value: string;
  new_value: string;
  old_value_display: string | null;
  new_value_display: string | null;
  changed_at: string;
  changed_by: string | null;
  changed_by_name: string | null;
  pipeline_stage: string | null;
  pipeline_stage_name: string | null;
  change_source: 'manual' | 'system' | 'automation' | 'webhook' | 'api';
}
